import type { SchemaGraphNode } from "../../../types/schemaAnalytics";

export type SchemaNodeColorStyle = {
  colorKey: string;
  border: string;
  background: string;
  swatch: string;
};

const OPENSEARCH_STYLE: SchemaNodeColorStyle = {
  colorKey: "OPENSEARCH",
  border: "#5B8FF9",
  background: "#F5F9FF",
  swatch: "#5B8FF9",
};

/** Soft, distinct fills + matching borders for adaptor groups (stable by hash). */
const ADAPTOR_PALETTE: Omit<SchemaNodeColorStyle, "colorKey">[] = [
  { border: "#1D9A6C", background: "#F0FBF6", swatch: "#1D9A6C" },
  { border: "#D97706", background: "#FFF8F0", swatch: "#D97706" },
  { border: "#0F766E", background: "#F0FAF9", swatch: "#0F766E" },
  { border: "#B45309", background: "#FFF7ED", swatch: "#B45309" },
  { border: "#0369A1", background: "#F0F9FF", swatch: "#0369A1" },
  { border: "#BE123C", background: "#FFF1F2", swatch: "#BE123C" },
  { border: "#4D7C0F", background: "#F7FEE7", swatch: "#4D7C0F" },
  { border: "#C2410C", background: "#FFF7ED", swatch: "#C2410C" },
  { border: "#0E7490", background: "#ECFEFF", swatch: "#0E7490" },
  { border: "#A16207", background: "#FEFCE8", swatch: "#A16207" },
  { border: "#15803D", background: "#F0FDF4", swatch: "#15803D" },
  { border: "#9A3412", background: "#FFF7ED", swatch: "#9A3412" },
];

const MANUAL_BORDER = "#F6BD16";

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/** Same grouping key as SchemaAnalyticsPage.relationGroup. */
export function schemaNodeColorKey(node: Pick<SchemaGraphNode, "adaptorId" | "indexName" | "nodeKind" | "objectName">): string {
  if (node.adaptorId) {
    return `ADAPTOR:${node.adaptorId}`;
  }
  if (node.indexName?.startsWith("adaptor:")) {
    const parts = node.indexName.split(":");
    if (parts.length >= 2 && parts[1]) {
      return `ADAPTOR:${parts[1]}`;
    }
  }
  if (node.nodeKind === "ADAPTOR" || Boolean(node.objectName)) {
    return `ADAPTOR:${node.indexName || "unknown"}`;
  }
  return "OPENSEARCH";
}

export function colorForSchemaGroup(colorKey: string): SchemaNodeColorStyle {
  if (colorKey === "OPENSEARCH" || !colorKey.startsWith("ADAPTOR:")) {
    return OPENSEARCH_STYLE;
  }
  const palette = ADAPTOR_PALETTE[hashString(colorKey) % ADAPTOR_PALETTE.length];
  return {
    colorKey,
    border: palette.border,
    background: palette.background,
    swatch: palette.swatch,
  };
}

export function colorForSchemaNode(node: SchemaGraphNode): SchemaNodeColorStyle {
  const base = colorForSchemaGroup(schemaNodeColorKey(node));
  if (node.source === "MANUAL") {
    return {
      ...base,
      border: MANUAL_BORDER,
    };
  }
  return base;
}

export type SchemaLegendEntry = {
  colorKey: string;
  label: string;
  swatch: string;
  border: string;
  background: string;
};

/** Unique legend rows: OpenSearch (if present) + one entry per adaptor. */
export function buildSchemaColorLegend(nodes: SchemaGraphNode[] | undefined): SchemaLegendEntry[] {
  if (!nodes?.length) {
    return [];
  }
  const byKey = new Map<string, SchemaLegendEntry>();
  for (const node of nodes) {
    const colorKey = schemaNodeColorKey(node);
    if (byKey.has(colorKey)) {
      continue;
    }
    const style = colorForSchemaGroup(colorKey);
    const label =
      colorKey === "OPENSEARCH"
        ? "OPENSEARCH"
        : node.adaptorName || node.adaptorType || colorKey.replace(/^ADAPTOR:/, "") || colorKey;
    byKey.set(colorKey, {
      colorKey,
      label,
      swatch: style.swatch,
      border: style.border,
      background: style.background,
    });
  }
  const entries = Array.from(byKey.values());
  entries.sort((a, b) => {
    if (a.colorKey === "OPENSEARCH") return -1;
    if (b.colorKey === "OPENSEARCH") return 1;
    return a.label.localeCompare(b.label);
  });
  return entries;
}

/**
 * Surface for a node/legend swatch. Light mode keeps the pastel fill; dark mode tints the
 * dark canvas with the group's border colour so labels stay readable in both themes.
 */
export function schemaSurface(
  style: Pick<SchemaNodeColorStyle, "border" | "background">,
  dark: boolean
): { background: string; color: string } {
  if (!dark) {
    return { background: style.background, color: "#1A1A1A" };
  }
  const hex = /^#[0-9a-fA-F]{6}$/.test(style.border) ? style.border : "#5B8FF9";
  // 0x2E ≈ 18% opacity over the dark canvas.
  return { background: `${hex}2E`, color: "#F5F5F5" };
}
